import firebase from 'firebase/compat/app';
import 'firebase/compat/auth';
import 'firebase/compat/messaging';

// Firebase configuration — set via VITE_ environment variables (see .env.example)
const firebaseConfig = {
  apiKey: import.meta.env.VITE_FIREBASE_API_KEY,
  authDomain: import.meta.env.VITE_FIREBASE_AUTH_DOMAIN,
  projectId: import.meta.env.VITE_FIREBASE_PROJECT_ID,
  appId: import.meta.env.VITE_FIREBASE_APP_ID,
  // Required by firebase.messaging(): getToken() fails without it.
  messagingSenderId: import.meta.env.VITE_FIREBASE_MESSAGING_SENDER_ID || '',
};

let firebaseApp: firebase.app.App;
let firebaseAuth: firebase.auth.Auth;

export function initFirebase() {
  if (firebaseApp) return { app: firebaseApp, auth: firebaseAuth };

  firebaseApp = firebase.initializeApp(firebaseConfig);
  firebaseAuth = firebase.auth();
  firebaseAuth.setPersistence(firebase.auth.Auth.Persistence.LOCAL);

  return { app: firebaseApp, auth: firebaseAuth };
}

export async function signInWithGoogle(): Promise<string> {
  const { auth } = initFirebase();
  const provider = new firebase.auth.GoogleAuthProvider();
  provider.setCustomParameters({ prompt: 'select_account' });
  const result = await auth.signInWithPopup(provider);
  const idToken = await result.user?.getIdToken();
  if (!idToken) throw new Error('Failed to get ID token');
  return idToken;
}

/**
 * Email/password sign-in used only by the E2E harness build
 * (VITE_E2E_PASSWORD_LOGIN=1). The production login screen does not expose
 * this flow; the flag is replaced at build time and is off by default.
 */
export async function signInWithEmailPassword(email: string, password: string): Promise<string> {
  const { auth } = initFirebase();
  const result = await auth.signInWithEmailAndPassword(email, password);
  const idToken = await result.user?.getIdToken();
  if (!idToken) throw new Error('Failed to get ID token');
  return idToken;
}

export async function sendSignInLink(email: string): Promise<void> {
  const { auth } = initFirebase();
  const actionCodeSettings = {
    url: window.location.origin + '/',
    handleCodeInApp: true,
  };
  await auth.sendSignInLinkToEmail(email, actionCodeSettings);
  window.localStorage.setItem('emailForSignIn', email);
}

export async function completeEmailLinkSignIn(): Promise<string | null> {
  const { auth } = initFirebase();

  if (!auth.isSignInWithEmailLink(window.location.href)) {
    return null;
  }

  let email = window.localStorage.getItem('emailForSignIn');
  if (!email) {
    email = window.prompt('Please enter your email to complete sign-in:');
  }
  if (!email) return null;

  const result = await auth.signInWithEmailLink(email, window.location.href);
  window.localStorage.removeItem('emailForSignIn');
  const idToken = await result.user?.getIdToken();
  return idToken || null;
}

/**
 * True when the current URL is a Firebase email sign-in link that still needs
 * completion. Used by main.ts to bypass auto-login so the link is always
 * processed (it may target a different account than a persisted session).
 */
export function isEmailSignInLink(): boolean {
  const { auth } = initFirebase();
  return auth.isSignInWithEmailLink(window.location.href);
}

/**
 * Email of the currently signed-in Firebase user (persisted LOCAL session),
 * or null. Used where the UI must reflect an existing session without minting
 * a gateway login (e.g. offering Logout on the landing screen).
 */
export function getCurrentUserEmail(): string | null {
  const { auth } = initFirebase();
  return auth.currentUser?.email ?? null;
}

export async function signOut(): Promise<void> {
  const { auth } = initFirebase();
  await auth.signOut();
}

// ── Token cache with proactive refresh ──────────────────────────
// Firebase ID tokens expire after 1 hour.  We keep a cached copy that
// is automatically refreshed via onIdTokenChanged so the API client
// never sends a stale token.
let _cachedIdToken: string | null = null;

/**
 * Start listening for ID token changes.  Called once at app startup.
 * Keeps _cachedIdToken up-to-date whenever Firebase refreshes the token.
 */
export function startTokenRefreshListener(): void {
  const { auth } = initFirebase();
  auth.onIdTokenChanged(async (user) => {
    if (user) {
      try {
        _cachedIdToken = await user.getIdToken();
      } catch {
        _cachedIdToken = null;
      }
    } else {
      _cachedIdToken = null;
    }
  });
}

/**
 * Resolve once Firebase has restored the persisted session (or the timeout
 * elapses). Right after a page load `auth.currentUser` is briefly null even
 * for a signed-in user because the persisted user is restored asynchronously
 * from IndexedDB — callers that read the token too early silently get nothing.
 */
let authReadyPromise: Promise<void> | null = null;

export function waitForAuthReady(timeoutMs = 5000): Promise<void> {
  const { auth } = initFirebase();
  if (auth.currentUser) return Promise.resolve();
  if (authReadyPromise) return authReadyPromise;

  authReadyPromise = new Promise<void>((resolve) => {
    let settled = false;
    let unsubscribe: (() => void) | null = null;
    let timer: ReturnType<typeof setTimeout> | undefined;
    const finish = () => {
      if (settled) return;
      settled = true;
      if (timer !== undefined) clearTimeout(timer);
      unsubscribe?.();
      resolve();
    };
    unsubscribe = auth.onAuthStateChanged(() => finish());
    timer = setTimeout(finish, timeoutMs);
  });
  return authReadyPromise;
}

/**
 * Get a fresh Firebase ID token for the currently signed-in user.
 * Waits for the persisted session to be restored first (a page load starts
 * with an empty `currentUser`), then forces a refresh from Firebase
 * (bypasses cache) to guarantee validity.
 * Returns null if no user is signed in or refresh fails.
 */
export async function getIdToken(forceRefresh = true): Promise<string | null> {
  await waitForAuthReady();
  const { auth } = initFirebase();
  const user = auth.currentUser;
  if (!user) return null;
  try {
    const token = await user.getIdToken(forceRefresh);
    if (token) _cachedIdToken = token;
    return token;
  } catch {
    return _cachedIdToken; // fall back to last-known-good token
  }
}

/**
 * Get the cached ID token (no network call).  Use getIdToken(true)
 * when you need a guaranteed-fresh token.
 */
export function getCachedIdToken(): string | null {
  return _cachedIdToken;
}

/**
 * Check if a Firebase user is already signed in (persisted via LOCAL persistence)
 * and return a fresh ID token. Returns null if no user is signed in.
 * Forces a token refresh to ensure the token is valid.
 */
export async function tryAutoLogin(): Promise<string | null> {
  const { auth } = initFirebase();

  // Wait for Firebase auth to initialize and resolve the current user
  await new Promise<void>((resolve) => {
    const unsubscribe = auth.onAuthStateChanged((_user) => {
      unsubscribe();
      resolve();
    });
  });

  const user = auth.currentUser;
  if (!user) return null;

  try {
    // forceRefresh=true ensures we get a valid, non-expired token
    const idToken = await user.getIdToken(true);
    return idToken;
  } catch {
    // Token refresh failed (e.g. session revoked) — user needs to re-sign-in
    return null;
  }
}

export { firebaseAuth };

// ── Firebase Cloud Messaging ──

let firebaseMessaging: firebase.messaging.Messaging | null = null;
let _fcmToken: string | null = null;
let _onFcmTokenRefresh: ((token: string) => void) | null = null;
let _lastFcmError = '';

/** Why the last registerFCM() attempt failed ('' when it succeeded or was
 *  never attempted). Surfaced by the PWA self-test so a "no notifications"
 *  report is actionable instead of a guess. */
export function getLastFcmError(): string {
  return _lastFcmError;
}

/**
 * Get or create the Firebase Messaging instance.
 */
export function getMessaging(): firebase.messaging.Messaging | null {
  try {
    if (!firebaseMessaging) {
      const { app } = initFirebase();
      firebaseMessaging = firebase.messaging(app);
    }
    return firebaseMessaging;
  } catch {
    // Messaging may not be supported (e.g., non-HTTPS in dev)
    return null;
  }
}

/**
 * Set a callback to be invoked when the FCM token is refreshed.
 */
export function onFcmTokenRefresh(callback: (token: string) => void): void {
  _onFcmTokenRefresh = callback;
}

/**
 * Get the current FCM token (cached).
 */
export function getCurrentFcmToken(): string | null {
  return _fcmToken;
}

/**
 * Request notification permission and register for FCM push.
 * Returns the FCM token if successful, or null if not supported/denied.
 *
 * The FCM service worker is registered at its OWN scope and receives the
 * Firebase config in its query string. It must not be the workbox `sw.js`
 * at '/', because that script has no push / notificationclick handlers.
 * Keeping the config in the SW URL also survives service-worker restarts
 * (an in-memory postMessage handshake does not).
 *
 * @param vapidKey — Firebase Web Push certificate key (Firebase Console →
 *   Cloud Messaging → Web Push certificates). Falls back to
 *   VITE_FIREBASE_VAPID_KEY.
 */
export async function registerFCM(vapidKey?: string): Promise<string | null> {
  _lastFcmError = '';
  if (!('Notification' in window)) {
    _lastFcmError = 'notifications not supported by this browser';
    console.log('[fcm] Notifications not supported in this browser');
    return null;
  }

  const key = (vapidKey || import.meta.env.VITE_FIREBASE_VAPID_KEY || '').trim();
  if (!key) {
    _lastFcmError = 'VAPID key missing from this build';
    console.warn(
      '[fcm] VITE_FIREBASE_VAPID_KEY is not configured — push notifications are disabled',
    );
    return null;
  }
  if (!import.meta.env.VITE_FIREBASE_MESSAGING_SENDER_ID) {
    _lastFcmError = 'messaging sender id missing from this build';
    console.warn(
      '[fcm] VITE_FIREBASE_MESSAGING_SENDER_ID is not configured — push notifications are disabled',
    );
    return null;
  }

  if (Notification.permission === 'denied') {
    _lastFcmError = 'notifications are blocked for this site';
    console.log('[fcm] Notification permission denied by the user');
    return null;
  }
  const permission =
    Notification.permission === 'granted'
      ? 'granted'
      : await Notification.requestPermission();
  if (permission !== 'granted') {
    _lastFcmError = `permission ${permission}`;
    console.log('[fcm] Notification permission not granted');
    return null;
  }

  const messaging = getMessaging();
  if (!messaging) {
    _lastFcmError = 'Firebase Messaging unavailable in this browser/context';
    console.warn('[fcm] Firebase Messaging not available');
    return null;
  }

  try {
    const swConfig = {
      apiKey: import.meta.env.VITE_FIREBASE_API_KEY,
      authDomain: import.meta.env.VITE_FIREBASE_AUTH_DOMAIN,
      projectId: import.meta.env.VITE_FIREBASE_PROJECT_ID,
      appId: import.meta.env.VITE_FIREBASE_APP_ID,
      messagingSenderId: import.meta.env.VITE_FIREBASE_MESSAGING_SENDER_ID,
    };
    const swUrl =
      '/firebase-messaging-sw.js?config=' + encodeURIComponent(JSON.stringify(swConfig));
    let registration: ServiceWorkerRegistration;
    try {
      registration = await navigator.serviceWorker.register(swUrl, {
        scope: '/firebase-cloud-messaging-push-scope',
      });
    } catch (err: any) {
      // Most common here: the SW script could not be fetched/parsed (offline,
      // blocked CDN imports, or a proxy rewriting the file).
      _lastFcmError = `service worker registration failed (${err?.name || 'Error'}: ${err?.message || err})`;
      throw err;
    }

    const tokenOptions: any = {
      vapidKey: key,
      serviceWorkerRegistration: registration,
    };

    _fcmToken = await messaging.getToken(tokenOptions);
    if (!_fcmToken) {
      _lastFcmError = 'FCM returned an empty token';
      return null;
    }
    console.log('[fcm] Token obtained:', _fcmToken?.slice(0, 8) + '...');

    // Token rotation. Firebase 12 removed onTokenRefresh() from the compat
    // Messaging object (it used to be here and threw "onTokenRefresh is not a
    // function", which aborted a SUCCESSFUL registration). Treat it as an
    // optional extra: _registerForPush() re-reads the token on every app start
    // and upserts it with the gateway, so a rotated token is picked up then.
    // Never let this failure discard the token we already have.
    const legacy = messaging as any;
    if (typeof legacy.onTokenRefresh === 'function') {
      try {
        legacy.onTokenRefresh(async () => {
          try {
            const newToken = await messaging.getToken(tokenOptions);
            if (!newToken) return;
            _fcmToken = newToken;
            console.log('[fcm] Token refreshed');
            if (_onFcmTokenRefresh) _onFcmTokenRefresh(newToken);
          } catch (err) {
            console.warn('[fcm] Token refresh failed:', err);
          }
        });
      } catch (err) {
        console.warn('[fcm] onTokenRefresh unavailable (token still valid):', err);
      }
    }

    return _fcmToken;
  } catch (err: any) {
    if (!_lastFcmError) {
      _lastFcmError = `FCM token request failed (${err?.code || err?.name || 'Error'}: ${err?.message || err})`;
    }
    console.warn('[fcm] Failed to get FCM token:', err);
    return null;
  }
}

/**
 * Listen for foreground messages (when the app is open).
 * Calls the provided callback with the message payload.
 */
export function onForegroundMessage(
  callback: (payload: any) => void,
): () => void {
  const messaging = getMessaging();
  if (!messaging) return () => {};

  const unsubscribe = messaging.onMessage((payload) => {
    console.log('[fcm] Foreground message:', payload);
    callback(payload);
  });

  return unsubscribe;
}

